import {createApi} from '@reduxjs/toolkit/query/react'
import {createMontBlancBaseQuery} from '@apis/helpers'
import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {MemberUserInterface, UserInterface, UserNameInterface} from '@interfaces/user'
import {matchUsersByEmail} from '@shared/utils/userOption'
import type {
  AzureConnectionStatusInterface,
  AzureSyncScheduledInterface,
  BillingAccountInterface,
  JoinRequestDecisionResponse,
  JoinRequestInterface,
  MemberTeamInterface,
  OrganizationDomainAvailability,
  OrganizationDomainInterface,
  OrganizationDomainJoinPolicy,
  OrganizationDomainVerifyResponse,
  OrganizationMemberInterface,
  ProvisionOrganizationRequest,
  ProvisionOrganizationResponse,
  RoleInterface,
  TenantCurrentUserInterface,
  TenantCurrentUserUpdateResponseInterface,
  TenantDetailInterface,
  TenantPermissionInterface,
  TenantSubscriptionAllocationInterface,
  TenantSubscriptionInterface,
  TenantTeamInterface,
  TenantUserSummaryInterface,
  UserTeamPermissionInterface,
  UserTeamRoleInterface,
} from '@interfaces/tenants'

interface UpdateBillingAccountRequest {
  id: string
  body: Partial<BillingAccountInterface>
}

interface UpdateSubscriptionAllocationRequest {
  id: string
  subscriptionId: string
  body: Partial<TenantSubscriptionAllocationInterface>
}

interface CreateSubscriptionAllocationRequest {
  subscriptionId: string
  body: {
    subscription: string
    organization: string
    signal_quota: number
    ai_seat_quota: number
  }
}

interface UpdateTenantRequest {
  id: string
  body: Partial<TenantDetailInterface>
}

interface UpdateTeamRequest {
  id: string
  body: Partial<TenantTeamInterface>
}

interface UpdateRoleRequest {
  id: string
  body: Partial<RoleInterface>
}

interface TeamUserRequest {
  teamId: string
  user: number | string
}

interface CreateUserTeamRoleRequest {
  user: number | string
  team: string
  role: string
}

interface CreateUserTeamPermissionRequest {
  user: number | string
  team: string
  permission: string
}

interface InviteTenantUserRequest {
  email: string
  first_name?: string
  last_name?: string
  // Defaults to the requester's current organization when omitted.
  organization?: string
  // Designated teams to join on top of the org's Default team.
  teams?: string[]
  // Optional explicit (team, role) grants.
  roles?: {team: string; role: string}[]
}

interface InviteTenantUserResponse {
  user_id: number | string
  email: string
  created: boolean
  organization: string
  teams: string[]
  role_assignments: number
}

interface UpdateOrganizationDomainRequest {
  id: string
  body: {join_policy?: OrganizationDomainJoinPolicy; default_role?: string | null}
}

interface UpdateTenantsCurrentUserRequest {
  body: Partial<TenantCurrentUserInterface> &
    Record<string, unknown> & {
      current_organization_id?: string
    }
}

// Page size for the per-user team membership lookup below; the organizations
// teams endpoint caps `limit` at 200.
const TEAM_MEMBERSHIP_PAGE_SIZE = 200

// The organizations `users/resolve/` endpoint rejects more than 100
// identity_uuids per request (400), so larger lookups are split into chunks.
const RESOLVE_USER_NAMES_CHUNK_SIZE = 100

// The organizations `users/?emails=` filter rejects more than 100 emails per
// request (400), so larger lookups are split into chunks.
const USERS_BY_EMAIL_CHUNK_SIZE = 100

// The member lookups -- `users/resolve/?ids=`/`?emails=` and
// `teams/directory/?ids=` -- take at most 100 values per request (400).
const MEMBER_LOOKUP_CHUNK_SIZE = 100

/** Stored recipients to put names to through `users/resolve/`, by what they are stored as. */
export interface MemberUserLookup {
  ids?: Array<number | string>
  identityUuids?: string[]
  emails?: string[]
}

export const infrastructureTenantsApi = createApi({
  reducerPath: 'infrastructureTenantsApi',
  baseQuery: createMontBlancBaseQuery('organizations'),
  tagTypes: [
    'BillingAccount',
    'Subscription',
    'SubscriptionAllocation',
    'Tenant',
    'Team',
    'Role',
    'User',
    'UserTeamRole',
    'UserTeamPermission',
    'AzureConnection',
    'Contact',
    'NotificationPreference',
    'NotificationSubscription',
    'NotificationMessage',
    'MsTeamsDestination',
    'OrganizationDomain',
    'JoinRequest',
  ],
  endpoints: (builder) => ({
    getTenantUsers: builder.query<
      BaseFetchResultsInterface<UserInterface>,
      {
        limit: number
        offset: number
        excludeSelf?: boolean
        ordering?: string
        search?: string
        /**
         * Restrict to these identity uuids (comma-separated) — the mirror of
         * `ids` on teams. Used where a screen holds uuids and needs names, which
         * `search` cannot answer: it takes one term and matches text. Paging
         * cannot answer it either, because the largest organization has ~1800
         * members and every other has fewer than twenty.
         */
        identityUuids?: string
        /**
         * Restrict to these user ids (comma-separated) — `identityUuids` by
         * primary key. Report and automation recipients are stored as user ids
         * (AB#6508), so that a member who has never signed in can be named.
         */
        ids?: string
        /**
         * Restrict to these emails (comma-separated, case-insensitive, at most
         * 100). Scheduled prompt recipients are stored as emails (AB#6634).
         */
        emails?: string
      }
    >({
      query: ({
        limit,
        offset,
        excludeSelf,
        ordering = 'first_name,last_name,email',
        search,
        identityUuids,
        ids,
        emails,
      }) => ({
        url: 'users/',
        params: {
          limit,
          offset,
          ordering,
          ...(excludeSelf ? {exclude_self: true} : {}),
          ...(search ? {search} : {}),
          ...(identityUuids ? {identity_uuids: identityUuids} : {}),
          ...(ids ? {ids} : {}),
          ...(emails ? {emails} : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({type: 'User' as const, id})),
              {type: 'User' as const, id: 'LIST'},
            ]
          : [{type: 'User' as const, id: 'LIST'}],
    }),
    /**
     * Names for identity_uuids of members of the CURRENT organization, for
     * labelling another user (chat authors, saved-view creators). Any member may
     * call it; unknown uuids are simply absent from the result. Callers pass the
     * uuids they hold (order them stably for a stable cache key); more than
     * RESOLVE_USER_NAMES_CHUNK_SIZE are fetched in sequential chunks.
     */
    resolveUserNames: builder.query<UserNameInterface[], string[]>({
      async queryFn(identityUuids, _queryApi, _extraOptions, fetchWithBQ) {
        const users: UserNameInterface[] = []
        for (let i = 0; i < identityUuids.length; i += RESOLVE_USER_NAMES_CHUNK_SIZE) {
          // Sequential on purpose: keeps one request in flight per lookup.
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchWithBQ({
            url: 'users/resolve/',
            params: {
              identity_uuids: identityUuids.slice(i, i + RESOLVE_USER_NAMES_CHUNK_SIZE).join(','),
            },
          })
          if (response.error) {
            return {error: response.error}
          }
          users.push(...((response.data as UserNameInterface[] | undefined) ?? []))
        }
        return {data: users}
      },
      providesTags: [{type: 'User' as const, id: 'LIST'}],
    }),
    /**
     * Members of the CURRENT organization found by name or email through
     * `users/resolve/?search=` (AB#6640): unlike `users/`, any member may call
     * it, so a people picker works for someone without user management. Only
     * members with an identity come back, at most one picker page, name and
     * email only.
     */
    searchMemberUsers: builder.query<UserNameInterface[], {search: string; excludeSelf?: boolean}>({
      query: ({search, excludeSelf}) => ({
        url: 'users/resolve/',
        params: {search, ...(excludeSelf ? {exclude_self: true} : {})},
      }),
      providesTags: [{type: 'User' as const, id: 'LIST'}],
    }),
    /**
     * Members of the CURRENT organization by name or email for a recipient
     * picker (AB#6704), through the same member-readable `users/resolve/?search=`
     * as `searchMemberUsers`, with the user id. `includeUnsigned` keeps members
     * who never signed in, for pickers that store user ids (AB#6508). One picker
     * page at most; send a non-blank term.
     */
    /**
     * Members of the CURRENT organization by name, a page at a time, through
     * the member-readable `users/directory/` (AB#6837): a people picker opens on
     * the first names A to Z, like every other picker, and loads more. Same
     * members and record as `searchMemberUsers`.
     */
    getMemberDirectory: builder.query<
      BaseFetchResultsInterface<MemberUserInterface>,
      {limit: number; search?: string; excludeSelf?: boolean}
    >({
      query: ({limit, search, excludeSelf}) => ({
        url: 'users/directory/',
        params: {
          limit,
          offset: 0,
          ...(search ? {search} : {}),
          ...(excludeSelf ? {exclude_self: true} : {}),
        },
      }),
      providesTags: [{type: 'User' as const, id: 'LIST'}],
    }),
    findMemberUsers: builder.query<
      MemberUserInterface[],
      {search: string; includeUnsigned?: boolean}
    >({
      query: ({search, includeUnsigned}) => ({
        url: 'users/resolve/',
        params: {search, ...(includeUnsigned ? {include_unsigned: true} : {})},
      }),
      providesTags: [{type: 'User' as const, id: 'LIST'}],
    }),
    /**
     * Members of the CURRENT organization by stored user ids, identity uuids or
     * emails, for naming saved recipients (AB#6704). Any member may call it,
     * unlike `users/?ids=`. Each kind is asked for separately and in chunks;
     * anybody outside the organization is simply absent.
     */
    resolveMemberUsers: builder.query<MemberUserInterface[], MemberUserLookup>({
      async queryFn(
        {ids = [], identityUuids = [], emails = []},
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const users: MemberUserInterface[] = []
        const lookups: Array<[string, Array<number | string>]> = [
          ['ids', ids],
          ['identity_uuids', identityUuids],
          ['emails', emails],
        ]
        for (const [param, values] of lookups) {
          for (let i = 0; i < values.length; i += MEMBER_LOOKUP_CHUNK_SIZE) {
            // Sequential on purpose: keeps one request in flight per lookup.
            // eslint-disable-next-line no-await-in-loop
            const response = await fetchWithBQ({
              url: 'users/resolve/',
              params: {[param]: values.slice(i, i + MEMBER_LOOKUP_CHUNK_SIZE).join(',')},
            })
            if (response.error) {
              return {error: response.error}
            }
            users.push(...((response.data as MemberUserInterface[] | undefined) ?? []))
          }
        }
        return {data: users}
      },
      providesTags: [{type: 'User' as const, id: 'LIST'}],
    }),
    /**
     * Teams of the CURRENT organization by name, through the member-readable
     * `teams/directory/?search=` (AB#6704): `teams/` shows a member only their
     * own teams. Id and name only, one picker page at most.
     */
    findMemberTeams: builder.query<MemberTeamInterface[], {search: string}>({
      query: ({search}) => ({url: 'teams/directory/', params: {search}}),
      providesTags: [{type: 'Team' as const, id: 'LIST'}],
    }),
    /** Names for stored team ids through `teams/directory/?ids=` (AB#6704), in chunks. */
    resolveMemberTeams: builder.query<MemberTeamInterface[], string[]>({
      async queryFn(ids, _queryApi, _extraOptions, fetchWithBQ) {
        const teams: MemberTeamInterface[] = []
        for (let i = 0; i < ids.length; i += MEMBER_LOOKUP_CHUNK_SIZE) {
          // Sequential on purpose: keeps one request in flight per lookup.
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchWithBQ({
            url: 'teams/directory/',
            params: {ids: ids.slice(i, i + MEMBER_LOOKUP_CHUNK_SIZE).join(',')},
          })
          if (response.error) {
            return {error: response.error}
          }
          teams.push(...((response.data as MemberTeamInterface[] | undefined) ?? []))
        }
        return {data: teams}
      },
      providesTags: [{type: 'Team' as const, id: 'LIST'}],
    }),
    /**
     * Directory records for stored emails (scheduled prompt recipients) through
     * `users/?emails=`, for naming them. More than USERS_BY_EMAIL_CHUNK_SIZE are
     * fetched in sequential chunks. Only users whose email was asked for are
     * kept: an organizations deploy without the filter ignores the parameter and
     * answers a plain page of the directory. Same CanViewUser gate as `users/`.
     */
    getTenantUsersByEmails: builder.query<UserInterface[], string[]>({
      async queryFn(emails, _queryApi, _extraOptions, fetchWithBQ) {
        const users: UserInterface[] = []
        for (let i = 0; i < emails.length; i += USERS_BY_EMAIL_CHUNK_SIZE) {
          const chunk = emails.slice(i, i + USERS_BY_EMAIL_CHUNK_SIZE)
          // Sequential on purpose: keeps one request in flight per lookup.
          // eslint-disable-next-line no-await-in-loop
          const response = await fetchWithBQ({
            url: 'users/',
            params: {limit: USERS_BY_EMAIL_CHUNK_SIZE, offset: 0, emails: chunk.join(',')},
          })
          if (response.error) {
            return {error: response.error}
          }
          const page = response.data as BaseFetchResultsInterface<UserInterface> | undefined
          users.push(...matchUsersByEmail(chunk, page?.results).values())
        }
        return {data: users}
      },
      providesTags: [{type: 'User' as const, id: 'LIST'}],
    }),
    inviteTenantUser: builder.mutation<InviteTenantUserResponse, InviteTenantUserRequest>({
      query: (body) => ({
        url: 'users/invite/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        {type: 'User' as const, id: 'LIST'},
        {type: 'Team' as const, id: 'LIST'},
        {type: 'UserTeamRole' as const, id: 'LIST'},
      ],
    }),
    // Remove org users (single + bulk) from the caller's CURRENT organization:
    // their membership, teams and roles there go, their other organizations are
    // untouched (AB#6737). Lives on the organizations API because the
    // account-management grid is sourced from this service, so the ids passed
    // here are organization user ids (the core service has a separate id space).
    // Getting a removed user back is a new invitation.
    removeUsersFromOrganization: builder.mutation<{removed: number}, {users: number[]}>({
      query: (body) => ({
        url: 'users/remove-from-organization/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        {type: 'User' as const, id: 'LIST'},
        {type: 'Team' as const, id: 'LIST'},
        {type: 'UserTeamRole' as const, id: 'LIST'},
      ],
    }),
    getTenantsCurrentUser: builder.query<TenantCurrentUserInterface, void>({
      query: () => ({url: 'current_user/'}),
    }),
    updateTenantsCurrentUser: builder.mutation<
      TenantCurrentUserUpdateResponseInterface,
      UpdateTenantsCurrentUserRequest
    >({
      query: ({body}) => ({
        url: 'current_user/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{type: 'Tenant' as const, id: 'LIST'}],
    }),
    // "View As": overlay the session with a set of teams' permissions in an org.
    // The caller performs a full reload afterwards, so the whole app re-fetches
    // as the impersonated member; the tag invalidations cover any cached user/org
    // data read before the reload lands.
    viewAsSession: builder.mutation<
      TenantCurrentUserInterface,
      {organization_id: string; user_id?: number | string; team_ids?: string[]}
    >({
      query: (body) => ({
        url: 'current_user/view-as/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        {type: 'Tenant' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
      ],
    }),
    resetSession: builder.mutation<TenantCurrentUserInterface, void>({
      query: () => ({
        url: 'current_user/reset-session/',
        method: 'POST',
      }),
      invalidatesTags: [
        {type: 'Tenant' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
      ],
    }),
    // Password-reset requests. The server resolves the target account from the
    // session (own reset) or the organization user id (admin reset of a member),
    // so no email is ever sent from the client. Nothing cached changes, hence no
    // tag invalidation.
    requestOwnPasswordReset: builder.mutation<{message: string}, void>({
      query: () => ({
        url: 'current_user/reset-password/',
        method: 'POST',
      }),
    }),
    requestUserPasswordReset: builder.mutation<{message: string}, {id: number}>({
      query: ({id}) => ({
        url: `users/${id}/reset-password/`,
        method: 'POST',
      }),
    }),
    // The current organization's domains (AB#6862), for its owners and admins:
    // add a claim, verify it by its DNS TXT record, set who may join from it,
    // remove it (members stay). Not paginated -- an organization has a handful.
    getOrganizationDomains: builder.query<OrganizationDomainInterface[], void>({
      query: () => ({url: 'organization-domains/'}),
      providesTags: [{type: 'OrganizationDomain' as const, id: 'LIST'}],
    }),
    addOrganizationDomain: builder.mutation<OrganizationDomainInterface, {domain: string}>({
      query: (body) => ({
        url: 'organization-domains/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{type: 'OrganizationDomain' as const, id: 'LIST'}],
    }),
    // Rate-limited server-side (one DNS check per row per 30 s); a repeat within
    // that window returns the last result.
    verifyOrganizationDomain: builder.mutation<OrganizationDomainVerifyResponse, {id: string}>({
      query: ({id}) => ({
        url: `organization-domains/${id}/verify/`,
        method: 'POST',
      }),
      invalidatesTags: [{type: 'OrganizationDomain' as const, id: 'LIST'}],
    }),
    updateOrganizationDomain: builder.mutation<
      OrganizationDomainInterface,
      UpdateOrganizationDomainRequest
    >({
      query: ({id, body}) => ({
        url: `organization-domains/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{type: 'OrganizationDomain' as const, id: 'LIST'}],
    }),
    deleteOrganizationDomain: builder.mutation<void, {id: string}>({
      query: ({id}) => ({
        url: `organization-domains/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{type: 'OrganizationDomain' as const, id: 'LIST'}],
    }),
    // Requests to join the current organization from an Approval domain
    // (AB#6862), for whoever may invite. Paginated like `users/`.
    getJoinRequests: builder.query<
      BaseFetchResultsInterface<JoinRequestInterface>,
      {limit: number; offset: number}
    >({
      query: ({limit, offset}) => ({
        url: 'join-requests/',
        params: {limit, offset},
      }),
      providesTags: [{type: 'JoinRequest' as const, id: 'LIST'}],
    }),
    // Approving makes the requester a member, so the users list changes too.
    // A 409 means someone else already decided it.
    approveJoinRequest: builder.mutation<JoinRequestDecisionResponse, {id: string}>({
      query: ({id}) => ({
        url: `join-requests/${id}/approve/`,
        method: 'POST',
      }),
      invalidatesTags: [
        {type: 'JoinRequest' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
      ],
    }),
    rejectJoinRequest: builder.mutation<JoinRequestDecisionResponse, {id: string}>({
      query: ({id}) => ({
        url: `join-requests/${id}/reject/`,
        method: 'POST',
      }),
      invalidatesTags: [{type: 'JoinRequest' as const, id: 'LIST'}],
    }),
    // Check whether an organization domain is well-formed and still free
    // (authoritative -- covers domains held by orgs the caller can't see).
    checkOrganizationDomain: builder.query<OrganizationDomainAvailability, {domain: string}>({
      query: ({domain}) => ({
        url: 'organization-domain-available/',
        params: {domain},
      }),
    }),
    // Provision a brand-new organization end to end (create org + allocation +
    // owner + the Default Team and its Default Role + core sync) in one atomic
    // backend call.
    provisionOrganization: builder.mutation<
      ProvisionOrganizationResponse,
      ProvisionOrganizationRequest
    >({
      query: (body) => ({
        url: 'provision-organization/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        {type: 'Tenant' as const, id: 'LIST'},
        {type: 'Subscription' as const, id: 'LIST'},
        {type: 'SubscriptionAllocation' as const, id: 'LIST'},
        {type: 'BillingAccount' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
      ],
    }),
    getOrganizations: builder.query<
      BaseFetchResultsInterface<TenantDetailInterface>,
      {
        limit: number
        offset: number
        ordering?: string
        status?: string
        search?: string
        currentOrganization?: string
      }
    >({
      query: ({limit, offset, ordering = 'name', status, search, currentOrganization}) => ({
        url: 'organizations/',
        params: {
          limit,
          offset,
          ordering,
          ...(status ? {status} : {}),
          ...(search ? {search} : {}),
          ...(currentOrganization ? {current_organization: currentOrganization} : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'Tenant' as const,
                id,
              })),
              {type: 'Tenant' as const, id: 'LIST'},
            ]
          : [{type: 'Tenant' as const, id: 'LIST'}],
    }),
    getOrganization: builder.query<TenantDetailInterface, {id: string}>({
      query: ({id}) => ({url: `organizations/${id}/`}),
      providesTags: (_result, _error, {id}) => [{type: 'Tenant' as const, id}],
    }),
    /**
     * One organization's members, paginated and searched on the server.
     *
     * The organization detail also carries `user_details`, but that is the whole
     * roster inline — one organization has ~1800 members. This is the pageable
     * form for pickers. The plain user listing cannot serve this screen: it is
     * hard-scoped to the requester's CURRENT organization, and subscription
     * management administers another one by id.
     */
    getOrganizationMembers: builder.query<
      BaseFetchResultsInterface<OrganizationMemberInterface>,
      {id: string; limit: number; offset: number; search?: string}
    >({
      query: ({id, limit, offset, search}) => ({
        url: `organizations/${id}/members/`,
        params: {limit, offset, ...(search ? {search} : {})},
      }),
      providesTags: (_result, _error, {id}) => [{type: 'Tenant' as const, id}],
    }),
    updateOrganization: builder.mutation<TenantDetailInterface, UpdateTenantRequest>({
      query: ({id, body}) => ({
        url: `organizations/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'Tenant' as const, id},
        {type: 'Tenant' as const, id: 'LIST'},
      ],
    }),
    getTenantTeams: builder.query<
      BaseFetchResultsInterface<TenantTeamInterface>,
      {
        limit: number
        offset: number
        ordering?: string
        search?: string
        organization?: string
        // Restrict to teams having any of these default roles (comma-separated ids).
        role?: string
        // Restrict to these team ids (comma-separated) — used by the resource-group
        // filter, which resolves a core resource group to its team ids client-side.
        ids?: string
        /**
         * Restrict to the teams this user (organizations pk) is a MEMBER of.
         * `search` and `ids` cannot answer it — the caller does not know the ids
         * yet — and neither can paging: one organization has ~1980 teams, so
         * scanning a page of the directory for the user's own memberships found
         * only the ones that happened to be on it.
         */
        user?: number | string
      }
    >({
      query: ({limit, offset, ordering = 'name', search, organization, role, ids, user}) => ({
        url: 'teams/',
        params: {
          limit,
          offset,
          ordering,
          ...(search ? {search} : {}),
          ...(organization ? {organization} : {}),
          ...(role ? {role} : {}),
          ...(ids ? {ids} : {}),
          ...(user !== undefined && user !== null && user !== '' ? {user} : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'Team' as const,
                id,
              })),
              {type: 'Team' as const, id: 'LIST'},
            ]
          : [{type: 'Team' as const, id: 'LIST'}],
    }),
    /**
     * The teams each of the given users belongs to, keyed by user id (as a
     * string), across as many pages as it takes.
     *
     * Exists for the subscription modal's access warning, which used to look
     * the users up inside ONE page of the team directory. One organization has
     * ~1980 teams; an owner/admin sees all of them, so the first 200 by name
     * never reached that customer's `X_*` tier teams and every named recipient
     * read as having no access (AB#6222). `?user=` answers membership on the
     * server, the same way the Users modal asks for it.
     *
     * Membership is small (dozens of teams per user), so this is normally one
     * request per user; the loop only exists so "small" is not an assumption
     * the UI depends on.
     */
    getTeamMembershipsForUsers: builder.query<
      {memberships: Record<string, string[]>},
      {userIds: Array<number | string>; organization?: string}
    >({
      async queryFn({userIds, organization}, _queryApi, _extraOptions, fetchWithBQ) {
        const memberships: Record<string, string[]> = {}
        for (const userId of userIds) {
          const teamIds: string[] = []
          let offset = 0
          for (;;) {
            // Sequential on purpose: each offset depends on the previous page.
            // eslint-disable-next-line no-await-in-loop
            const response = await fetchWithBQ({
              url: 'teams/',
              params: {
                user: userId,
                limit: TEAM_MEMBERSHIP_PAGE_SIZE,
                offset,
                ordering: 'name',
                ...(organization ? {organization} : {}),
              },
            })
            if (response.error) {
              return {error: response.error}
            }
            const page = response.data as BaseFetchResultsInterface<TenantTeamInterface>
            const rows = page.results || []
            teamIds.push(...rows.map((team) => String(team.id)))
            // Stop on a short/empty page as well as on the count, so a server
            // that disagrees with its own `count` cannot spin this loop.
            if (!rows.length || teamIds.length >= (page.count ?? teamIds.length)) {
              break
            }
            offset += TEAM_MEMBERSHIP_PAGE_SIZE
          }
          memberships[String(userId)] = teamIds
        }
        return {data: {memberships}}
      },
      providesTags: [{type: 'Team' as const, id: 'LIST'}],
    }),
    updateTenantTeam: builder.mutation<TenantTeamInterface, UpdateTeamRequest>({
      query: ({id, body}) => ({
        url: `teams/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'Team' as const, id},
        {type: 'Team' as const, id: 'LIST'},
      ],
    }),
    deleteTenantTeam: builder.mutation<void, {id: string}>({
      query: ({id}) => ({
        url: `teams/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'Team' as const, id},
        {type: 'Team' as const, id: 'LIST'},
      ],
    }),
    createTenantTeam: builder.mutation<TenantTeamInterface, Partial<TenantTeamInterface>>({
      query: (body) => ({
        url: 'teams/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{type: 'Team' as const, id: 'LIST'}],
    }),
    setDefaultTeam: builder.mutation<
      Record<string, unknown>,
      {organizationId: string; teamId: string}
    >({
      query: ({organizationId, teamId}) => ({
        url: `organizations/${organizationId}/set-default-team/`,
        method: 'POST',
        body: {team_id: teamId},
      }),
      invalidatesTags: [{type: 'Team' as const, id: 'LIST'}],
    }),
    getTenantRoles: builder.query<
      BaseFetchResultsInterface<RoleInterface>,
      {limit: number; offset: number; ordering?: string; search?: string; organization?: string}
    >({
      query: ({limit, offset, ordering = 'name', search, organization}) => ({
        url: 'roles/',
        params: {
          limit,
          offset,
          ordering,
          ...(search ? {search} : {}),
          ...(organization ? {organization} : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'Role' as const,
                id,
              })),
              {type: 'Role' as const, id: 'LIST'},
            ]
          : [{type: 'Role' as const, id: 'LIST'}],
    }),
    createTenantRole: builder.mutation<RoleInterface, Partial<RoleInterface>>({
      query: (body) => ({
        url: 'roles/',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{type: 'Role' as const, id: 'LIST'}],
    }),
    updateTenantRole: builder.mutation<RoleInterface, UpdateRoleRequest>({
      query: ({id, body}) => ({
        url: `roles/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'Role' as const, id},
        {type: 'Role' as const, id: 'LIST'},
      ],
    }),
    deleteTenantRole: builder.mutation<void, {id: string}>({
      query: ({id}) => ({
        url: `roles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'Role' as const, id},
        {type: 'Role' as const, id: 'LIST'},
      ],
    }),
    getUserTeamRoles: builder.query<
      BaseFetchResultsInterface<UserTeamRoleInterface>,
      {
        user?: number | string
        organization?: string
        limit?: number
        offset?: number
        ordering?: string
        search?: string
      }
    >({
      query: ({user, organization, limit = 25, offset = 0, ordering = 'team__name', search}) => {
        const params: Record<string, number | string> = {limit, offset, ordering}
        if (user !== undefined && user !== null && user !== '') params.user = user
        if (organization) params.organization = organization
        if (search) params.search = search
        return {url: 'user-team-roles/', params}
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'UserTeamRole' as const,
                id,
              })),
              {type: 'UserTeamRole' as const, id: 'LIST'},
            ]
          : [{type: 'UserTeamRole' as const, id: 'LIST'}],
    }),
    createUserTeamRole: builder.mutation<UserTeamRoleInterface, CreateUserTeamRoleRequest>({
      query: (body) => ({
        url: 'user-team-roles/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {user}) => [
        {type: 'UserTeamRole' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
        {type: 'User' as const, id: user},
      ],
    }),
    deleteUserTeamRole: builder.mutation<void, {id: string; user: number | string}>({
      query: ({id}) => ({
        url: `user-team-roles/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {user}) => [
        {type: 'UserTeamRole' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
        {type: 'User' as const, id: user},
      ],
    }),
    getUserTeamPermissions: builder.query<
      BaseFetchResultsInterface<UserTeamPermissionInterface>,
      {
        user: number | string
        organization?: string
        limit?: number
        offset?: number
        ordering?: string
        search?: string
      }
    >({
      query: ({user, organization, limit = 25, offset = 0, ordering = 'team__name', search}) => {
        const params: Record<string, number | string> = {user, limit, offset, ordering}
        if (organization) params.organization = organization
        if (search) params.search = search
        return {url: 'user-team-permissions/', params}
      },
      // Provides its own UserTeamPermission tag plus UserTeamRole, so assigning or
      // removing a role/team (which cascades role-sourced permissions) still
      // refetches the effective-permission list.
      providesTags: [
        {type: 'UserTeamPermission' as const, id: 'LIST'},
        {type: 'UserTeamRole' as const, id: 'LIST'},
      ],
    }),
    createUserTeamPermission: builder.mutation<
      UserTeamPermissionInterface,
      CreateUserTeamPermissionRequest
    >({
      query: (body) => ({
        url: 'user-team-permissions/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {user}) => [
        {type: 'UserTeamPermission' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
        {type: 'User' as const, id: user},
      ],
    }),
    deleteUserTeamPermission: builder.mutation<void, {id: string; user: number | string}>({
      query: ({id}) => ({
        url: `user-team-permissions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {user}) => [
        {type: 'UserTeamPermission' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
        {type: 'User' as const, id: user},
      ],
    }),
    /**
     * Organization members who could join this team — paginated + searched on the
     * server. The membership exclusion is server-side, so paging can never
     * surface someone who is already a member.
     */
    getTenantTeamUserCandidates: builder.query<
      BaseFetchResultsInterface<TenantUserSummaryInterface>,
      {teamId: string; limit: number; offset: number; search?: string}
    >({
      query: ({teamId, limit, offset, search}) => ({
        url: `teams/${teamId}/user-candidates/`,
        params: {limit, offset, ...(search ? {search} : {})},
      }),
      providesTags: (_result, _error, {teamId}) => [
        {type: 'Team' as const, id: `USER-CANDIDATES-${teamId}`},
      ],
    }),
    addTenantTeamUser: builder.mutation<Record<string, unknown>, TeamUserRequest>({
      query: ({teamId, user}) => ({
        url: `teams/${teamId}/add-user/`,
        method: 'POST',
        body: {user},
      }),
      // Adding a user to a team cascades roles/permissions server-side, so
      // refresh the user's team-role/permission queries too.
      invalidatesTags: (_result, _error, {teamId, user}) => [
        {type: 'Team' as const, id: teamId},
        {type: 'Team' as const, id: 'LIST'},
        {type: 'Team' as const, id: `USER-CANDIDATES-${teamId}`},
        {type: 'UserTeamRole' as const, id: 'LIST'},
        {type: 'User' as const, id: user},
      ],
    }),
    removeTenantTeamUser: builder.mutation<Record<string, unknown>, TeamUserRequest>({
      query: ({teamId, user}) => ({
        url: `teams/${teamId}/remove-user/`,
        method: 'POST',
        body: {user},
      }),
      // Removing a user from a team cascades role/permission removal
      // server-side, so refresh the user's team-role/permission queries too.
      invalidatesTags: (_result, _error, {teamId, user}) => [
        {type: 'Team' as const, id: teamId},
        {type: 'Team' as const, id: 'LIST'},
        {type: 'Team' as const, id: `USER-CANDIDATES-${teamId}`},
        {type: 'UserTeamRole' as const, id: 'LIST'},
        {type: 'User' as const, id: user},
      ],
    }),
    // --- Azure Entra ID directory sync -------------------------------------
    getOrganizationAzureConnection: builder.query<
      AzureConnectionStatusInterface,
      {organizationId: string}
    >({
      query: ({organizationId}) => ({
        url: `organizations/${organizationId}/azure-connection/`,
      }),
      providesTags: (_result, _error, {organizationId}) => [
        {type: 'AzureConnection' as const, id: organizationId},
      ],
    }),
    connectOrganizationAzure: builder.mutation<
      AzureConnectionStatusInterface,
      {organizationId: string}
    >({
      query: ({organizationId}) => ({
        url: `organizations/${organizationId}/azure-connection/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, {organizationId}) => [
        {type: 'AzureConnection' as const, id: organizationId},
      ],
    }),
    verifyOrganizationAzure: builder.mutation<
      AzureConnectionStatusInterface,
      {organizationId: string}
    >({
      query: ({organizationId}) => ({
        url: `organizations/${organizationId}/azure-connection/verify/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, {organizationId}) => [
        {type: 'AzureConnection' as const, id: organizationId},
      ],
    }),
    // Org-wide directory sync. The scope is the organization's own
    // `sync_full_azure_tenant` setting, resolved server-side — the caller only
    // decides whether net-new users are emailed an invitation.
    syncOrganizationAzureTeams: builder.mutation<
      AzureSyncScheduledInterface,
      {organizationId: string; sendInvitationEmails?: boolean}
    >({
      query: ({organizationId, sendInvitationEmails}) => ({
        url: `organizations/${organizationId}/sync-azure-teams/`,
        method: 'POST',
        ...(sendInvitationEmails === undefined
          ? {}
          : {body: {send_invitation_emails: sendInvitationEmails}}),
      }),
      // A sync creates teams and org members wholesale, so the user roster has
      // to re-validate alongside the team list.
      invalidatesTags: [
        {type: 'Team' as const, id: 'LIST'},
        {type: 'User' as const, id: 'LIST'},
      ],
    }),
    // Per-team sync: refresh one team's memberships from its Azure group.
    syncTeamAzureMembers: builder.mutation<AzureSyncScheduledInterface, {teamId: string}>({
      query: ({teamId}) => ({
        url: `teams/${teamId}/sync-azure-members/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, {teamId}) => [
        {type: 'Team' as const, id: teamId},
        {type: 'Team' as const, id: `USER-CANDIDATES-${teamId}`},
        {type: 'UserTeamRole' as const, id: 'LIST'},
      ],
    }),
    getTenantPermissions: builder.query<
      BaseFetchResultsInterface<TenantPermissionInterface>,
      {limit: number; offset: number; ordering?: string; search?: string}
    >({
      // The viewset searches codename, name, description and category.
      query: ({limit, offset, ordering = 'category', search}) => ({
        url: 'permissions/',
        params: {limit, offset, ordering, ...(search ? {search} : {})},
      }),
    }),
    getBillingAccounts: builder.query<
      BaseFetchResultsInterface<BillingAccountInterface>,
      {limit: number; offset: number; ordering?: string; search?: string}
    >({
      query: ({limit, offset, ordering = 'name', search}) => ({
        url: 'billing-accounts/',
        params: {limit, offset, ordering, ...(search ? {search} : {})},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'BillingAccount' as const,
                id,
              })),
              {type: 'BillingAccount' as const, id: 'LIST'},
            ]
          : [{type: 'BillingAccount' as const, id: 'LIST'}],
    }),
    getBillingAccount: builder.query<BillingAccountInterface, {id: string}>({
      query: ({id}) => ({url: `billing-accounts/${id}/`}),
      providesTags: (_result, _error, {id}) => [{type: 'BillingAccount' as const, id}],
    }),
    getTenantSubscriptions: builder.query<
      BaseFetchResultsInterface<TenantSubscriptionInterface>,
      {
        limit: number
        offset: number
        ordering?: string
        search?: string
        billingAccount?: string[]
      }
    >({
      query: ({limit, offset, ordering = 'name', search, billingAccount}) => ({
        url: 'subscriptions/',
        params: {
          limit,
          offset,
          ordering,
          ...(search ? {search} : {}),
          ...(billingAccount?.length ? {billing_account: billingAccount.join(',')} : {}),
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'Subscription' as const,
                id,
              })),
              {type: 'Subscription' as const, id: 'LIST'},
            ]
          : [{type: 'Subscription' as const, id: 'LIST'}],
    }),
    getTenantSubscription: builder.query<TenantSubscriptionInterface, {id: string}>({
      query: ({id}) => ({url: `subscriptions/${id}/`}),
      providesTags: (_result, _error, {id}) => [{type: 'Subscription' as const, id}],
    }),
    getTenantSubscriptionAllocations: builder.query<
      BaseFetchResultsInterface<TenantSubscriptionAllocationInterface>,
      {
        limit: number
        offset: number
        ordering?: string
        subscriptionId?: string
        organization?: string
        search?: string
      }
    >({
      query: ({
        limit,
        offset,
        ordering = 'subscription__name',
        subscriptionId,
        organization,
        search,
      }) => {
        const params: Record<string, number | string> = {limit, offset, ordering}
        if (subscriptionId) {
          params.subscription = subscriptionId
        }
        if (organization) {
          params.organization = organization
        }
        if (search) {
          params.search = search
        }
        return {
          url: 'subscription-allocations/',
          params,
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({id}) => ({
                type: 'SubscriptionAllocation' as const,
                id,
              })),
              {type: 'SubscriptionAllocation' as const, id: 'LIST'},
            ]
          : [{type: 'SubscriptionAllocation' as const, id: 'LIST'}],
    }),
    createTenantSubscriptionAllocation: builder.mutation<
      TenantSubscriptionAllocationInterface,
      CreateSubscriptionAllocationRequest
    >({
      query: ({body}) => ({
        url: 'subscription-allocations/',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {subscriptionId}) => [
        {type: 'SubscriptionAllocation' as const, id: 'LIST'},
        {type: 'Subscription' as const, id: subscriptionId},
        {type: 'Subscription' as const, id: 'LIST'},
      ],
    }),
    updateTenantSubscriptionAllocation: builder.mutation<
      TenantSubscriptionAllocationInterface,
      UpdateSubscriptionAllocationRequest
    >({
      query: ({id, body}) => ({
        url: `subscription-allocations/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, {id, subscriptionId}) => [
        {type: 'SubscriptionAllocation' as const, id},
        {type: 'SubscriptionAllocation' as const, id: 'LIST'},
        {type: 'Subscription' as const, id: subscriptionId},
        {type: 'Subscription' as const, id: 'LIST'},
      ],
    }),
    deleteTenantSubscriptionAllocation: builder.mutation<
      void,
      {id: string; subscriptionId: string}
    >({
      query: ({id}) => ({
        url: `subscription-allocations/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, {id, subscriptionId}) => [
        {type: 'SubscriptionAllocation' as const, id},
        {type: 'SubscriptionAllocation' as const, id: 'LIST'},
        {type: 'Subscription' as const, id: subscriptionId},
        {type: 'Subscription' as const, id: 'LIST'},
      ],
    }),
    updateBillingAccount: builder.mutation<BillingAccountInterface, UpdateBillingAccountRequest>({
      query: ({id, body}) => ({
        url: `billing-accounts/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'BillingAccount' as const, id},
        {type: 'BillingAccount' as const, id: 'LIST'},
      ],
    }),
  }),
})

export const {
  useAddTenantTeamUserMutation,
  useCreateTenantTeamMutation,
  useCreateTenantRoleMutation,
  useCreateTenantSubscriptionAllocationMutation,
  useCreateUserTeamPermissionMutation,
  useCreateUserTeamRoleMutation,
  useDeleteTenantRoleMutation,
  useDeleteTenantTeamMutation,
  useDeleteUserTeamPermissionMutation,
  useDeleteTenantSubscriptionAllocationMutation,
  useDeleteUserTeamRoleMutation,
  useGetBillingAccountQuery,
  useGetBillingAccountsQuery,
  useGetTenantPermissionsQuery,
  useLazyGetTenantPermissionsQuery,
  useGetTenantRolesQuery,
  useLazyGetTenantRolesQuery,
  useGetOrganizationQuery,
  useGetOrganizationMembersQuery,
  useLazyGetOrganizationMembersQuery,
  useGetTenantSubscriptionAllocationsQuery,
  useGetTenantSubscriptionQuery,
  useGetTenantSubscriptionsQuery,
  useGetTenantTeamsQuery,
  useGetTeamMembershipsForUsersQuery,
  useLazyGetTenantTeamsQuery,
  useGetTenantTeamUserCandidatesQuery,
  useLazyGetTenantTeamUserCandidatesQuery,
  useGetTenantUsersQuery,
  useGetTenantUsersByEmailsQuery,
  useLazyGetTenantUsersQuery,
  useResolveUserNamesQuery,
  useLazyResolveUserNamesQuery,
  useSearchMemberUsersQuery,
  useGetMemberDirectoryQuery,
  useFindMemberUsersQuery,
  useResolveMemberUsersQuery,
  useFindMemberTeamsQuery,
  useResolveMemberTeamsQuery,
  useGetUserTeamPermissionsQuery,
  useGetUserTeamRolesQuery,
  useLazyGetUserTeamRolesQuery,
  useGetOrganizationsQuery,
  useInviteTenantUserMutation,
  useRemoveUsersFromOrganizationMutation,
  useRemoveTenantTeamUserMutation,
  useSetDefaultTeamMutation,
  useLazyGetTenantsCurrentUserQuery,
  useProvisionOrganizationMutation,
  useCheckOrganizationDomainQuery,
  useUpdateOrganizationMutation,
  useUpdateTenantRoleMutation,
  useUpdateTenantTeamMutation,
  useUpdateTenantSubscriptionAllocationMutation,
  useUpdateTenantsCurrentUserMutation,
  useViewAsSessionMutation,
  useResetSessionMutation,
  useRequestOwnPasswordResetMutation,
  useRequestUserPasswordResetMutation,
  useUpdateBillingAccountMutation,
  useGetOrganizationAzureConnectionQuery,
  useConnectOrganizationAzureMutation,
  useVerifyOrganizationAzureMutation,
  useSyncOrganizationAzureTeamsMutation,
  useSyncTeamAzureMembersMutation,
  useGetOrganizationDomainsQuery,
  useAddOrganizationDomainMutation,
  useVerifyOrganizationDomainMutation,
  useUpdateOrganizationDomainMutation,
  useDeleteOrganizationDomainMutation,
  useGetJoinRequestsQuery,
  useApproveJoinRequestMutation,
  useRejectJoinRequestMutation,
} = infrastructureTenantsApi
